import { useCallback, useEffect, useRef, useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { meApi } from '../services/api';
import { usePlatformEvents } from '../contexts/PlatformEventContext';
import { useAuth } from '../store/auth-context';
import type { ActivityItem, ActivityScope } from '../types';
import type { PlatformEvent } from '../types/events';

const PAGE_SIZE = 30;
const REALTIME_CAP = 100;

export function useActivities(scope?: ActivityScope) {
  const { subscribe, isConnected, diagnostics } = usePlatformEvents();
  const { user } = useAuth();
  const [realtimeItems, setRealtimeItems] = useState<ActivityItem[]>([]);
  const seenIdsRef = useRef(new Set<string>());

  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error,
  } = useInfiniteQuery({
    queryKey: ['me', 'activities', scope ?? 'spaces'],
    queryFn: async ({ pageParam }: { pageParam: string | undefined }) => {
      return meApi.activities({
        limit: PAGE_SIZE,
        cursor: pageParam,
        scope,
        expand: ['summary', 'metadata'],
      });
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextToken,
    // Always refetch on mount so activities created while this page was
    // unmounted (e.g. user was on another tab) appear immediately.
    staleTime: 0,
    retry: false,
  });

  // Cursors are mode-specific; flipping scope must drop realtime state too,
  // otherwise items from the previous mode leak across the switch.
  useEffect(() => {
    setRealtimeItems([]);
    seenIdsRef.current = new Set<string>();
  }, [scope]);

  // Build a set of all REST activity IDs for dedup
  const restIds = new Set<string>();
  if (data) {
    for (const page of data.pages) {
      for (const item of page.items) {
        restIds.add(item.activityId);
      }
    }
  }

  // Populate seenIdsRef with REST IDs
  useEffect(() => {
    if (data) {
      for (const page of data.pages) {
        for (const item of page.items) {
          seenIdsRef.current.add(item.activityId);
        }
      }
    }
  }, [data]);

  // Subscribe to real-time activity events.
  // The WS event envelope carries `eventId` / `subjectId` as the activity ID
  // and activity fields are spread across `event.data` (with `activitySource`
  // instead of `source`). Map them into an ActivityItem for the UI.
  const handleEvent = useCallback((event: PlatformEvent) => {
    const d = event.data as Record<string, unknown>;
    const activityId = (d.activityId as string) || event.subjectId || event.eventId;
    if (!activityId) return;

    if (seenIdsRef.current.has(activityId)) return;

    const eventSpaceId = (d.spaceId as string) ?? '';
    const eventUserId = d.userId as string | undefined;

    // Mirror the backend scope filter so realtime doesn't surface rows the
    // REST call would have hidden.
    if (scope === 'mine' && eventUserId !== user?.userId) return;
    if (scope && scope.startsWith('spc-') && eventSpaceId !== scope) return;

    seenIdsRef.current.add(activityId);

    const activity: ActivityItem = {
      activityId,
      orgId: event.orgId,
      spaceId: eventSpaceId,
      userId: eventUserId,
      source: ((d.activitySource ?? d.source ?? 'system') as ActivityItem['source']),
      triggerSource: d.triggerSource as ActivityItem['triggerSource'],
      category: (d.category as string) ?? '',
      priority: (d.priority as ActivityItem['priority']) ?? 'normal',
      visibility: (d.visibility as ActivityItem['visibility']) ?? 'org',
      title: (d.title as string) ?? '',
      summary: d.summary as string | undefined,
      relatedEntityIds: d.relatedEntityIds as ActivityItem['relatedEntityIds'],
      metadata: d.metadata as Record<string, unknown> | undefined,
      createdAt: event.metadata?.timestamp ?? new Date().toISOString(),
    };

    setRealtimeItems((prev) => {
      const next = [activity, ...prev];
      return next.length > REALTIME_CAP ? next.slice(0, REALTIME_CAP) : next;
    });
  }, [scope, user?.userId]);

  useEffect(() => {
    const unsub = subscribe([{ eventType: 'activity.created' }], handleEvent);
    return unsub;
  }, [subscribe, handleEvent]);

  // Merge: realtime items first (filtered to exclude any that later appeared in REST),
  // then REST items (filtered to exclude any that arrived via WS first)
  const restItems = data?.pages.flatMap((p) => p.items) ?? [];
  const realtimeIds = new Set(realtimeItems.map((i) => i.activityId));
  const dedupedRest = restItems.filter((i) => !realtimeIds.has(i.activityId));
  const activities = [...realtimeItems, ...dedupedRest];

  return {
    activities,
    isLoading,
    isFetchingNextPage,
    hasNextPage: hasNextPage ?? false,
    fetchNextPage,
    realtimeCount: realtimeItems.length,
    isConnected,
    diagnostics,
    error,
  };
}
